
import type { FunctionComponent } from 'react';
import { useNavigate, useParams } from 'react-router';

import Throbber from 'components/Throbber';
import { AdminPage } from 'components/admin';
import BlogItemEditor from 'components/admin/BlogItemEditor';

import useBlogItems from 'hooks/blog/useBlogItems';
import useSetBlogItem from 'hooks/blog/useSetBlogItem';

import { genericVideoUpload } from 'services';
import { createBlogItem } from 'services/blog/create';
import { updateBlogItem } from 'services/blog/update';
import uploadImage from 'services/uploadImage';

import {
  type BlogItemT,
  E_ADD_NEW,
  P_ADMIN_BLOG,
  getId,
  useGlobalState,
} from 'utils';

import S from './style.scss';

const AdminBlogItemEditorPage: FunctionComponent = () => {
  const [perms] = useGlobalState('user.permissions');
  if (!perms?.BLOG_EDITOR) throw Error('Forbidden');

  const { id } = useParams();
  const isNew = !id || id === E_ADD_NEW.slice(1);
  const blogItems = useBlogItems(isNew ? [] : [id]);
  const blogItem = isNew ? undefined : blogItems.items[id];

  const navigate = useNavigate();

  const setBlogItem = useSetBlogItem();

  const save = async (
    item: BlogItemT,

    /** Header image or video. */
    media: File | string | undefined,

    onProgress: (progress: number) => void,
  ): Promise<BlogItemT> => {
    const update: BlogItemT = {
      ...item,

      // eslint-disable-next-line no-underscore-dangle
      __v: blogItem?.__v,
    };

    if (typeof media === 'string') {
      throw Error('Media import from URL is not supported yet');
    }

    if (media) {
      if (media.type.startsWith('image/')) {
        const res = await uploadImage(media, {
          convertToJpeg: true,

          // NOTE: This is the size of thumbnails in blog post summary cards,
          // in the blog content listing.
          createThumbnailOfSize: 265,

          maxSize: 1024,
        });

        update.image = res.image;
        update.imageThumbnail = res.thumbnail;
        update.isHeadMediaReady = true;
      } else if (media.type.startsWith('video/')) {
        const res = await genericVideoUpload({
          file: media,
          name: `[BLOG HEAD] ${item.title}`,
          onProgress,
        });

        update.image = res.link;
        update.imageThumbnail = res.pictures.base_link;
        update.isHeadMediaReady = false;
      }
    }

    if (id === E_ADD_NEW.slice(1)) {
      // Create new blog post.
      const blog = await createBlogItem(update);

      // TODO: Here, if the new item has been created, it should be added to
      // the front of the currently loaded items page.
      setBlogItem(blog);

      void navigate(`${P_ADMIN_BLOG}/${getId(blog)}`);

      return blog;
    }

    // eslint-disable-next-line no-underscore-dangle
    if (id === undefined || update.__v === undefined) {
      throw Error('Internal error');
    }

    const blog = await updateBlogItem(id, update);

    setBlogItem(blog);

    return blog;
  };

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle={isNew ? 'Add New Post' : 'Edit Post'}
      title={isNew ? 'Blog / Add New Post' : 'Blog / Edit Post'}
    >
      <div styleName="content">
        {isNew || blogItem ? (
          <BlogItemEditor
            initialState={blogItem}
            save={save}
          />
        ) : <Throbber withOverlay />}
      </div>
    </AdminPage>
  );
};

export default AdminBlogItemEditorPage;
